import { useTranslation } from 'react-i18next';
// hooks/useTickets.js - ACTUALIZADO: Hook para gestión de tickets usando api.js
import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { useNotification } from '../components/common/GlobalNotifications';
import { api, ticketHelpers, handleApiError } from '../services/api';

export const useTickets = () => {
  const { t } = useTranslation();
    const { currentCompany } = useAuth();
    const { showError, showSuccess } = useNotification();

    // State
    const [tickets, setTickets] = useState([]);
    const [activeTickets, setActiveTickets] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch tickets with filters usando api.js
    const fetchTickets = useCallback(async (filters = {}) => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                include_stats: 'true',
                limit: filters.limit || '50',
                offset: filters.offset || '0',
                ...filters
            };

            const response = await api.alarmTickets.getWithStats(params);

            setTickets(response.data?.tickets || []);
            setStats(response.data?.stats || null);

            return {
                success: true,
                tickets: response.data?.tickets || [],
                stats: response.data?.stats || null,
                totalCount: response.data?.total_count || 0
            };
        } catch (error) {
            const apiError = handleApiError(error);
            setError(apiError.message);
            showError(t('hardcoded.hooks_usetickets.error_cargando_tickets_message', { message: apiError.message }));
            return { success: false, error: apiError.message };
        } finally {
            setLoading(false);
        }
    }, [showError]);

    // Fetch active tickets for audio control usando api.js
    const fetchActiveTickets = useCallback(async () => {
        try {
            const response = await api.alarmTickets.getActiveForAudio();
            setActiveTickets(response.data || []);
            return response.data || [];
        } catch (error) {
            console.error('Error fetching active tickets:', error);
            return [];
        }
    }, []);

    // Get single ticket by ID usando api.js
    const getTicket = useCallback(async (ticketId) => {
        try {
            const response = await api.alarmTickets.getById(ticketId);
            return { success: true, ticket: response.data };
        } catch (error) {
            const apiError = handleApiError(error);
            return { success: false, error: apiError.message };
        }
    }, []);

    // Acknowledge tickets (mark as viewed) usando api.js
    const acknowledgeTickets = useCallback(async (ticketIds) => {
        try {
            const response = await api.alarmTickets.acknowledge(ticketIds);

            // Refresh active tickets after acknowledging
            await fetchActiveTickets();

            // Show success message
            const count = Array.isArray(ticketIds) ? ticketIds.length : 1;
            showSuccess(t('hardcoded.hooks_usetickets.count_ticketcount1s_marcadocount1s_como_vistocount1s', { count: count, count1s: count > 1 ? 's' : '', count1s: count > 1 ? 's' : '', count1s: count > 1 ? 's' : '' }));

            return { success: true, data: response.data };
        } catch (error) {
            const apiError = handleApiError(error);
            showError(t('hardcoded.hooks_usetickets.error_marcando_tickets_como_vistos', { message: apiError.message }));
            return { success: false, error: apiError.message };
        }
    }, [fetchActiveTickets, showError, showSuccess]);

    // Acknowledge all tickets for a vehicle usando api.js
    const acknowledgeVehicleTickets = useCallback(async (vehicleId) => {
        try {
            const response = await api.alarmTickets.acknowledgeVehicle(vehicleId);

            // Refresh active tickets after acknowledging
            await fetchActiveTickets();

            // Show success message
            showSuccess(t('hardcoded.hooks_usetickets.tickets_del_vehiculo_marcados_como'));

            return { success: true, data: response.data };
        } catch (error) {
            const apiError = handleApiError(error);
            showError(t('hardcoded.hooks_usetickets.error_marcando_tickets_del_vehiculo', { message: apiError.message }));
            return { success: false, error: apiError.message };
        }
    }, [fetchActiveTickets, showError, showSuccess]);

    // Resolve ticket manually usando api.js
    const resolveTicket = useCallback(async (ticketId, resolutionNotes) => {
        try {
            const response = await api.alarmTickets.resolve(ticketId, resolutionNotes);

            // Refresh active tickets after resolving
            await fetchActiveTickets();

            // Show success message
            showSuccess(t('hardcoded.hooks_usetickets.ticket_resuelto_exitosamente'));

            return { success: true, data: response.data };
        } catch (error) {
            const apiError = handleApiError(error);
            showError(t('hardcoded.hooks_usetickets.error_resolviendo_ticket_message', { message: apiError.message }));
            return { success: false, error: apiError.message };
        }
    }, [fetchActiveTickets, showError, showSuccess]);

    // Get ticket statistics usando api.js
    const getStats = useCallback(async () => {
        try {
            const response = await api.alarmTickets.getStats();
            setStats(response.data);
            return { success: true, stats: response.data };
        } catch (error) {
            const apiError = handleApiError(error);
            return { success: false, error: apiError.message };
        }
    }, []);

    // Filter active tickets by vehicle
    const getActiveTicketsByVehicle = useCallback((vehicleId) => {
        return activeTickets.filter(ticket => ticket.vehicle_id === vehicleId);
    }, [activeTickets]);

    // Get open tickets count
    const getOpenTicketsCount = useCallback(() => {
        return activeTickets.filter(ticket => ticket.status === 'open').length;
    }, [activeTickets]);

    // Get acknowledged tickets count
    const getAcknowledgedTicketsCount = useCallback(() => {
        return activeTickets.filter(ticket => ticket.status === 'acknowledged').length;
    }, [activeTickets]);

    // Check if vehicle has open tickets
    const vehicleHasOpenTickets = useCallback((vehicleId) => {
        return activeTickets.some(ticket =>
            ticket.vehicle_id === vehicleId && ticket.status === 'open'
        );
    }, [activeTickets]);

    // Check if vehicle has any tickets (open or acknowledged)
    const vehicleHasActiveTickets = useCallback((vehicleId) => {
        return activeTickets.some(ticket =>
            ticket.vehicle_id === vehicleId &&
            (ticket.status === 'open' || ticket.status === 'acknowledged')
        );
    }, [activeTickets]);

    // 🆕 NUEVOS: Métodos adicionales usando ticketHelpers

    // Bulk acknowledge tickets
    const bulkAcknowledgeTickets = useCallback(async (ticketIds) => {
        try {
            const result = await ticketHelpers.bulkProcessTickets(ticketIds, 'acknowledge');

            if (result.success) {
                await fetchActiveTickets();
                showSuccess(t('hardcoded.hooks_usetickets.successcount_tickets_marcados_como_vistos', { successCount: result.successCount }));

                if (result.errorCount > 0) {
                    showError(`${result.errorCount} tickets fallaron al ser procesados`);
                }
            }

            return result;
        } catch (error) {
            const apiError = handleApiError(error);
            showError(t('hardcoded.hooks_usetickets.error_en_proceso_masivo_message', { message: apiError.message }));
            return { success: false, error: apiError.message };
        }
    }, [fetchActiveTickets, showSuccess, showError]);

    // Bulk resolve tickets
    const bulkResolveTickets = useCallback(async (ticketIds, resolutionNotes) => {
        try {
            const result = await ticketHelpers.bulkProcessTickets(ticketIds, t('hardcoded.hooks_usetickets.resolve'), resolutionNotes);

            if (result.success) {
                await fetchActiveTickets();
                showSuccess(t('hardcoded.hooks_usetickets.successcount_tickets_resueltos', { successCount: result.successCount }));

                if (result.errorCount > 0) {
                    showError(t('hardcoded.hooks_usetickets.errorcount_tickets_fallaron_al_ser', { errorCount: result.errorCount }));
                }
            }

            return result;
        } catch (error) {
            const apiError = handleApiError(error);
            showError(t('hardcoded.hooks_usetickets.error_en_resolucion_masiva_message', { message: apiError.message }));
            return { success: false, error: apiError.message };
        }
    }, [fetchActiveTickets, showSuccess, showError]);

    // Search tickets with advanced filters
    const searchTickets = useCallback(async (filters = {}) => {
        try {
            setLoading(true);
            const result = await ticketHelpers.searchTickets(filters);

            if (result.success) {
                setTickets(result.tickets);
                setStats(result.stats);

                return {
                    success: true,
                    tickets: result.tickets,
                    stats: result.stats,
                    totalCount: result.totalCount
                };
            }

            return result;
        } catch (error) {
            const apiError = handleApiError(error);
            setError(apiError.message);
            return { success: false, error: apiError.message };
        } finally {
            setLoading(false);
        }
    }, []);

    // Get tickets by status
    const getTicketsByStatus = useCallback(async (status) => {
        try {
            const response = await api.alarmTickets.getByStatus(status);
            return {
                success: true,
                tickets: response.data?.tickets || []
            };
        } catch (error) {
            const apiError = handleApiError(error);
            return { success: false, error: apiError.message };
        }
    }, []);

    // Get tickets by vehicle
    const getTicketsByVehicle = useCallback(async (vehicleId) => {
        try {
            const response = await api.alarmTickets.getByVehicle(vehicleId);
            return {
                success: true,
                tickets: response.data?.tickets || []
            };
        } catch (error) {
            const apiError = handleApiError(error);
            return { success: false, error: apiError.message };
        }
    }, []);

    // Format helpers - usar ticketHelpers
    const formatDuration = useCallback((minutes) => {
        return ticketHelpers.formatDuration(minutes);
    }, []);

    const getStatusColor = useCallback((status) => {
        return ticketHelpers.getStatusColor(status);
    }, []);

    const getStatusLabel = useCallback((status) => {
        return ticketHelpers.getStatusLabel(status);
    }, []);

    // Auto-refresh active tickets every 10 seconds
    useEffect(() => {
        let interval;

        if (currentCompany) {
            // Initial fetch
            fetchActiveTickets();

            // Set up interval
            interval = setInterval(() => {
                fetchActiveTickets();
            }, 10000); // 10 seconds
        }

        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [currentCompany, fetchActiveTickets]);

    return {
        // State
        tickets,
        activeTickets,
        stats,
        loading,
        error,

        // Core actions
        fetchTickets,
        fetchActiveTickets,
        getTicket,
        acknowledgeTickets,
        acknowledgeVehicleTickets,
        resolveTicket,
        getStats,

        // Advanced actions
        bulkAcknowledgeTickets,
        bulkResolveTickets,
        searchTickets,
        getTicketsByStatus,
        getTicketsByVehicle,

        // Computed values
        getActiveTicketsByVehicle,
        getOpenTicketsCount,
        getAcknowledgedTicketsCount,
        vehicleHasOpenTickets,
        vehicleHasActiveTickets,

        // Formatting helpers
        formatDuration,
        getStatusColor,
        getStatusLabel,

        // Helper methods
        refreshActiveTickets: fetchActiveTickets,
        clearError: () => setError(null)
    };
};